/**
 * useListKeyboardNav — navegación por teclado ↑/↓ en listados del config.
 *
 * Mueve el índice seleccionado con las flechas y activa (abre detalle) con Enter.
 * Se deshabilita solo (enabled=false) cuando hay un panel de detalle full-screen abierto:
 * en ese caso las flechas las maneja TabbedDetailPanel (prev/next item).
 *
 * Ver: docs/design-system/COMPONENT_CATALOG.md
 */
import { useEffect } from 'react';

interface Opts {
  count: number;
  selectedIndex: number;
  onChange: (i: number) => void;
  /** Se dispara con Enter sobre el item seleccionado (típicamente: abrir detalle). */
  onActivate?: (i: number) => void;
  /** Si false, el hook no escucha (ej. detalle abierto o input con foco). */
  enabled?: boolean;
}

export function useListKeyboardNav({
  count,
  selectedIndex,
  onChange,
  onActivate,
  enabled = true,
}: Opts): void {
  useEffect(() => {
    if (!enabled || count === 0) return;

    const handler = (e: KeyboardEvent) => {
      // No secuestrar flechas mientras el foco está en un input/textarea/select.
      const el = document.activeElement;
      const tag = el?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        onChange(Math.min(count - 1, (selectedIndex < 0 ? -1 : selectedIndex) + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        onChange(Math.max(0, (selectedIndex < 0 ? 1 : selectedIndex) - 1));
      } else if (e.key === 'Enter' && selectedIndex >= 0 && onActivate) {
        e.preventDefault();
        onActivate(selectedIndex);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [count, selectedIndex, onChange, onActivate, enabled]);
}

export default useListKeyboardNav;
